import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/common/Loading.component';
import Alert from '../../../components/common/Alert.component';
import ConfirmDialog from '../../../components/common/Confirm.component';
import DetailView from '../../../views/Detail.view';
import Section from '../../../components/detail/components/Section.component';
import _ from 'lodash';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuidv4 } from 'uuid';
import IconDownlaodComponent from '../../../components/detail/components/IconDownload.component';
import { fetchAPI } from '../../../api/request';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
//import SendIcon from '@mui/icons-material/Send';
import { API as mexalAPI } from '../../../module/mexal';

const EditButton = ({ to }) => {
  const { id } = useParams();

  return (
    <Link to={to} state={{ id: id }}>
      <IconButton color='primary'>
        <EditIcon />
      </IconButton>
    </Link>
  );
};

const DeliveryNoteDetailPage = ({ module, deliveryNoteModule }) => {
  const navigate = useNavigate();
  const { detail, tag, code } = module;
  const { query, detail: detailDeliveryNote } = deliveryNoteModule;
  const { jwt } = useSelector((state) => state.login);
  const userAuth = useSelector((state) => state.login?.user);
  const {
    loading,
    data,
    error: errorDetail,
  } = useSelector(detail.selector.data);
  const [error, setError] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState(false);

  const onError = useCallback(() => {
    setError(true);
  }, [setError]);

  const onOpenSendDialog = useCallback((item) => {
    setSelectedDeliveryNote(item);
    setSendDialogOpen(true);
  }, []);

  const onCloseSendDialog = useCallback(() => {
    if (!sendLoading) {
      setSendDialogOpen(false);
    }
  }, [sendLoading]);

  const onConfirmSendDeliveryNote = useCallback(() => {
    if (!selectedDeliveryNote?.id) {
      setSendError({ message: 'Delivery note id is missing' });
      setSendDialogOpen(false);
      return;
    }

    setSendLoading(true);
    setSendSuccess(false);
    setSendError(false);

    fetchAPI(
      mexalAPI.sendDeliveryNote,
      {},
      '',
      {
        data: {
          id_delivery_note: selectedDeliveryNote.id,
          department: code,
        },
      },
      jwt
    )
      .then((response) => {
        setSendSuccess(
          response?.message || 'Delivery note sent to Mexal successfully'
        );
        setSendDialogOpen(false);
      })
      .catch((err) => {
        setSendError(err);
        setSendDialogOpen(false);
      })
      .finally(() => {
        setSendLoading(false);
      });
  }, [selectedDeliveryNote, code, jwt]);

  useEffect(() => {
    if (data?.id)
      fetchAPI(query?.all?.settings, { id: data?.id }, '', null, jwt)
        .then((resp) => setDeliveryNotes(resp?.data))
        .catch(onError);
  }, [data, jwt, query, onError, setDeliveryNotes]);

  useEffect(() => {
    if (
      error &&
      errorDetail &&
      (errorDetail?.response?.status === 404 ||
        errorDetail?.response?.status === 500)
    )
      navigate('/404');
  }, [errorDetail, error, navigate]);

  return (
    <Fragment>
      {loading && <Loading />}
      {sendSuccess && <Alert severity='success'>{sendSuccess}</Alert>}
      {sendError && (
        <Alert severity='error'>
          {sendError?.response?.data?.error?.message ||
            sendError?.message ||
            'An error has occurred'}
        </Alert>
      )}

      <ConfirmDialog
        open={sendDialogOpen}
        onClose={onCloseSendDialog}
        title={sendLoading ? 'Sending' : 'Confirm Send'}
        label='Send'
        cancelAction={onCloseSendDialog}
        confirmAction={onConfirmSendDeliveryNote}
        color='primary'
        disabled={sendLoading}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: '450px',
            minHeight: '30px',
          }}
        >
          {sendLoading && <CircularProgress size={24} />}
          <Typography>
            {sendLoading
              ? 'Sending delivery note to Mexal...'
              : 'Are you sure you want to send this delivery note to Mexal?'}
          </Typography>
        </Box>
      </ConfirmDialog>

      {userAuth.permissions[_.toUpper(tag)]?.READ === 'true' &&
        !_.isEmpty(deliveryNotes) && (
          <Box mt={2}>
            <Section label='Delivery Note'>
              {deliveryNotes?.map((item) => {
                const noteNumber = `${item?.attributes?.delivery_note_number}`;
                const id_logbook = _.get(item, 'attributes.id_logbook.data.id');


                const fields = deliveryNoteModule?.detail.conf?.fields.map(
                  (item) => {
                    const newField = _.cloneDeep(item);
                    if (item?.id === 'delivery_notes')
                      _.set(
                        newField,
                        'label',
                        `Delivery Note Number: ${noteNumber}`
                      );
                    return newField;
                  }
                );

                return (
                  <Box
                    key={uuidv4()}
                    mb={2}
                    mt={1}
                    sx={{ position: 'relative' }}
                  >
                    <Stack
                      direction={'row'}
                      mt={1.7}
                      sx={{ position: 'absolute', top: 0, right: 0 }}
                    >
                      {id_logbook && (
                        <Tooltip title='Logbook'>
                          <Link to={`/logbook/${id_logbook}`}>
                            <IconButton color='primary'>
                              <LibraryBooksIcon />
                            </IconButton>
                          </Link>
                        </Tooltip>
                      )}


                      <IconDownlaodComponent
                        value={item}
                        module={{
                          ...deliveryNoteModule,
                          query: {
                            ...deliveryNoteModule.query,
                            PDF: {
                              settings: {
                                ...deliveryNoteModule.query.PDF.settings,
                                path: `${deliveryNoteModule.query.PDF.settings.path}?department=${code}`,
                              },
                            },
                          },
                        }}
                        title={`Download PDF BC_${noteNumber}`}
                      />

                      {/* <Tooltip title='Send Delivery Note to Mexal'>
                        <IconButton
                          color='primary'
                          onClick={() => onOpenSendDialog(item)}
                          disabled={sendLoading}
                        >
                          <SendIcon />
                        </IconButton>
                      </Tooltip> */}

                      {detail?.edit &&
                        userAuth.permissions[_.toUpper(tag)]?.UPDATE ===
                        'true' && (
                          <EditButton to={`deliveryNote/${item?.id}/edit`} />
                        )}
                    </Stack>
                    <DetailView
                      data={item}
                      conf={{
                        ...detailDeliveryNote.conf,
                        fields: fields,
                      }}
                    />
                  </Box>
                );
              })}
            </Section>
          </Box>
        )}
    </Fragment>
  );
};

export default DeliveryNoteDetailPage;
